// ===========================================
// SERVICE: Etiquetas de cliente (CORE) — ORR-364
// ===========================================

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import { fetchAllPages, splitIntoBatches } from '@/lib/postgrest-chunks'

/** Derivada de los tipos generados: `created_at`/`updated_at` son nullable en la tabla. */
export type EtiquetaCliente = Database['public']['Tables']['etiquetas_cliente']['Row']

/** Clientes en los que la acción terminó bien y cuántos fallaron. */
export interface ResultadoEnLote {
  hechos: string[]
  fallos: number
}

/** Cantidad de escrituras que se mandan en paralelo en una acción sobre varios clientes. */
const TANDA_EN_LOTE = 10

/** Corre la acción para cada cliente en tandas de `TANDA_EN_LOTE`. Un fallo no corta el resto. */
const enTandas = async (
  clienteIds: string[],
  accion: (clienteId: string) => Promise<void>
): Promise<ResultadoEnLote> => {
  const hechos: string[] = []
  let fallos = 0
  for (const tanda of splitIntoBatches(clienteIds, TANDA_EN_LOTE)) {
    const resultados = await Promise.allSettled(tanda.map(accion))
    resultados.forEach((r, i) => {
      if (r.status === 'fulfilled') hechos.push(tanda[i])
      else fallos++
    })
  }
  return { hechos, fallos }
}

export const etiquetasClienteService = {
  /** Catálogo de la organización, ordenado como se muestra. */
  async listar(
    supabase: SupabaseClient<Database>,
    organizacionId: string
  ): Promise<EtiquetaCliente[]> {
    const { data, error } = await supabase.from('etiquetas_cliente')
      .select('*')
      .eq('organizacion_id', organizacionId)
      .order('nombre', { ascending: true })

    if (error) throw error
    return data ?? []
  },

  async crear(
    supabase: SupabaseClient<Database>,
    input: { organizacionId: string; nombre: string; createdBy: string }
  ): Promise<EtiquetaCliente> {
    const { data, error } = await supabase.from('etiquetas_cliente')
      .insert({
        organizacion_id: input.organizacionId,
        // El único que sobrevive al viaje: la base normaliza para el índice único, pero lo que se
        // muestra es lo que se escribió.
        nombre: input.nombre.trim(),
        created_by: input.createdBy,
      })
      .select()
      .single()

    if (error) throw error
    return data
  },

  /**
   * Cuántos clientes llevan la etiqueta y cuántas tareas que se repiten la usan como grupo.
   * Las repeticiones se cuentan activas y pausadas, porque cualquiera de las dos impide borrarla.
   */
  async contarUsos(
    supabase: SupabaseClient<Database>,
    organizacionId: string,
    etiquetaId: string
  ): Promise<{ clientes: number; repeticiones: number }> {
    const [clientes, repeticiones] = await Promise.all([
      supabase.from('cliente_etiqueta')
        .select('id', { count: 'exact', head: true })
        .eq('organizacion_id', organizacionId)
        .eq('etiqueta_id', etiquetaId),
      supabase.from('tareas_recurrentes')
        .select('id', { count: 'exact', head: true })
        .eq('organizacion_id', organizacionId)
        .eq('etiqueta_id', etiquetaId),
    ])

    if (clientes.error) throw clientes.error
    if (repeticiones.error) throw repeticiones.error
    return { clientes: clientes.count ?? 0, repeticiones: repeticiones.count ?? 0 }
  },

  /**
   * Borra la etiqueta del catálogo. La base se la quita a todos los clientes que la llevan.
   * Si una tarea que se repite la usa como grupo, la base rechaza el borrado con 23503.
   */
  async eliminar(
    supabase: SupabaseClient<Database>,
    organizacionId: string,
    etiquetaId: string
  ): Promise<void> {
    const { error } = await supabase.from('etiquetas_cliente')
      .delete()
      .eq('id', etiquetaId)
      .eq('organizacion_id', organizacionId)

    if (error) throw error
  },

  /** Ids de las etiquetas que lleva un cliente. El nombre lo resuelve el catálogo ya cargado. */
  async listarIdsDeCliente(
    supabase: SupabaseClient<Database>,
    clienteId: string
  ): Promise<string[]> {
    const { data, error } = await supabase.from('cliente_etiqueta')
      .select('etiqueta_id')
      .eq('cliente_id', clienteId)

    if (error) throw error
    return (data ?? []).map((f) => f.etiqueta_id)
  },

  async asignar(
    supabase: SupabaseClient<Database>,
    input: { organizacionId: string; clienteId: string; etiquetaId: string; createdBy: string }
  ): Promise<void> {
    const { error } = await supabase.from('cliente_etiqueta').insert({
      organizacion_id: input.organizacionId,
      cliente_id: input.clienteId,
      etiqueta_id: input.etiquetaId,
      created_by: input.createdBy,
    })

    // 23505 = ya la tenía. Dos clics rápidos sobre el mismo checkbox no son un error para el
    // usuario: el estado final es el que pidió.
    if (error && (error as { code?: string }).code !== '23505') throw error
  },

  async desasignar(
    supabase: SupabaseClient<Database>,
    clienteId: string,
    etiquetaId: string
  ): Promise<void> {
    const { error } = await supabase.from('cliente_etiqueta')
      .delete()
      .eq('cliente_id', clienteId)
      .eq('etiqueta_id', etiquetaId)

    if (error) throw error
  },

  /** Pone una etiqueta a varios clientes. */
  asignarEnLote(
    supabase: SupabaseClient<Database>,
    input: { organizacionId: string; clienteIds: string[]; etiquetaId: string; createdBy: string }
  ): Promise<ResultadoEnLote> {
    return enTandas(input.clienteIds, (clienteId) =>
      etiquetasClienteService.asignar(supabase, {
        organizacionId: input.organizacionId,
        clienteId,
        etiquetaId: input.etiquetaId,
        createdBy: input.createdBy,
      })
    )
  },

  /** Quita una etiqueta a varios clientes. */
  quitarEnLote(
    supabase: SupabaseClient<Database>,
    input: { clienteIds: string[]; etiquetaId: string }
  ): Promise<ResultadoEnLote> {
    return enTandas(input.clienteIds, (clienteId) =>
      etiquetasClienteService.desasignar(supabase, clienteId, input.etiquetaId)
    )
  },

  /**
   * Etiquetas de TODOS los clientes de la organización, en una sola consulta paginada.
   * Alimenta la columna y el filtro de la base de clientes: nunca una consulta por fila.
   */
  async listarPorOrganizacion(
    supabase: SupabaseClient<Database>,
    organizacionId: string
  ): Promise<Map<string, string[]>> {
    const filas = await fetchAllPages<{ cliente_id: string; etiqueta_id: string }>(
      async (desde, hasta) => {
        const { data, error } = await supabase.from('cliente_etiqueta')
          .select('cliente_id, etiqueta_id')
          .eq('organizacion_id', organizacionId)
          // Orden estable por PK: sin él, dos páginas del mismo `.range()` pueden repetir o
          // saltear filas, y el mapa quedaría incompleto sin ningún error visible.
          .order('id')
          .range(desde, hasta)

        if (error) throw error
        return data ?? []
      }
    )

    const map = new Map<string, string[]>()
    for (const fila of filas) {
      const arr = map.get(fila.cliente_id) ?? []
      arr.push(fila.etiqueta_id)
      map.set(fila.cliente_id, arr)
    }
    return map
  },

  /**
   * Lleva las etiquetas de un cliente al conjunto pedido: asigna las que faltan y desasigna las
   * que sobran, en paralelo. Un fallo en una no aborta el resto — el caller decide qué hacer con
   * el conteo de fallos, sin que la operación que lo trajo (alta o edición del cliente) se revierta.
   */
  async reconciliar(
    supabase: SupabaseClient<Database>,
    input: {
      organizacionId: string
      clienteId: string
      idsActuales: string[]
      idsFinal: string[]
      createdBy: string
    }
  ): Promise<{ fallos: number; asignadas: string[] }> {
    const actuales = new Set(input.idsActuales)
    const final = new Set(input.idsFinal)
    const aAsignar = input.idsFinal.filter((id) => !actuales.has(id))
    const aQuitar = input.idsActuales.filter((id) => !final.has(id))

    const resultados = await Promise.allSettled([
      ...aAsignar.map((etiquetaId) =>
        etiquetasClienteService.asignar(supabase, {
          organizacionId: input.organizacionId,
          clienteId: input.clienteId,
          etiquetaId,
          createdBy: input.createdBy,
        })
      ),
      ...aQuitar.map((etiquetaId) =>
        etiquetasClienteService.desasignar(supabase, input.clienteId, etiquetaId)
      ),
    ])

    // Los primeros `aAsignar.length` resultados corresponden a `aAsignar`, en el mismo orden.
    const asignadas = aAsignar.filter((_, i) => resultados[i].status === 'fulfilled')

    return {
      fallos: resultados.filter((r) => r.status === 'rejected').length,
      asignadas,
    }
  },
}
